import {ControllerFlowAPI} from '@wix/yoshi-flow-editor'
import {
  GetScheduledTimeSlotsRequest,
  TimeSlot,
} from '@wix/ambassador-table-reservations-v1-time-slot/types'
import {getScheduledTimeSlots as getScheduledTimeSlotsService} from '@wix/ambassador-table-reservations-v1-time-slot/http'
import {
  addMilliseconds,
  subMilliseconds,
  startOfDay,
  endOfDay,
  subMinutes,
  addMinutes,
  setMilliseconds,
  setSeconds,
} from 'date-fns'
import {utcToZonedTime, zonedTimeToUtc} from '@wix/table-reservations-lib/timezone'

import {EXPERIMENTS, TransactionNames} from '../../utils/constants'

import {addNonWorkingHoursTimeSlots} from './nonWorkingHoursTimeSlots'

// 24h - a period from "selected date - 12:00:00.000" to "selected time + 11:59:59.999"
// day - a period from 00:00:00.000 to 23:59:59.999 of selected date
type Period = 'day' | '24h'

const HOURS_IN_DAY = 24
const MINUTES_IN_HOUR = 60
const SECONDS_IN_MINUTE = 60
const MILLISECONDS_IN_SECOND = 1000

const MILLISECONDS_IN_DAY =
  HOURS_IN_DAY * MINUTES_IN_HOUR * SECONDS_IN_MINUTE * MILLISECONDS_IN_SECOND
const HALF_MILLISECONDS_IN_DAY = MILLISECONDS_IN_DAY / 2

// max period of requested timeslots is 23:59:59.999
const MILLISECONDS_IN_DAY_THRESHOLD = MILLISECONDS_IN_DAY - 1

const MINUTES_IN_DAY = 1440
const MINUTES_IN_HALF_DAY = MINUTES_IN_DAY / 2

export interface GetScheduledTimeSlotsForPeriodParams {
  reservationLocationId: string
  partySize: number
  date: Date
  period: Period
  timeSlotInterval?: number | null
  timeZone?: string | null
  experienceId?: string
}

export const getScheduledTimeSlotsForPeriod = async (
  flowAPI: ControllerFlowAPI,
  {
    reservationLocationId,
    partySize,
    date,
    timeSlotInterval,
    period,
    timeZone,
    experienceId,
  }: GetScheduledTimeSlotsForPeriodParams,
) => {
  const requestParams = generateGetScheduledTimeSlotsRequest({
    reservationLocationId,
    partySize,
    date,
    period,
    timeZone,
    experienceId,
    dayLightSavingTimeCalculationEnabled: flowAPI.experiments.enabled(
      EXPERIMENTS.dayLightSavingTimeCalculation,
    ),
  })

  const timeSlots = await getScheduledTimeSlots(flowAPI, requestParams)

  if (!timeSlots.length) {
    return []
  }

  const normalizedTimeSlots: TimeSlot[] = normalizeTimeSlots({timeSlots, timeZone})

  if (timeSlotInterval) {
    return addNonWorkingHoursTimeSlots({
      timeSlots: normalizedTimeSlots,
      timeSlotInterval,
    })
  }

  return normalizedTimeSlots
}

const generateGetScheduledTimeSlotsRequest = ({
  reservationLocationId,
  partySize,
  date,
  period,
  timeZone,
  experienceId,
  dayLightSavingTimeCalculationEnabled,
}: {
  reservationLocationId: string
  partySize: number
  date: Date
  period: Period
  timeZone?: string | null
  experienceId?: string
  dayLightSavingTimeCalculationEnabled: boolean
}): GetScheduledTimeSlotsRequest => {
  if (dayLightSavingTimeCalculationEnabled) {
    let startDate = new Date(date)
    let endDate = new Date(date)

    if (period === 'day') {
      startDate = startOfDay(startDate)
      endDate = endOfDay(endDate)
    } else {
      startDate = subMilliseconds(startDate, HALF_MILLISECONDS_IN_DAY)
      endDate = addMilliseconds(endDate, HALF_MILLISECONDS_IN_DAY - 1)
    }

    if (timeZone) {
      startDate = zonedTimeToUtc(startDate, timeZone)
      endDate = zonedTimeToUtc(endDate, timeZone)
    }

    const millisecondsBetweenEndAndStartDates = endDate.getTime() - startDate.getTime()

    if (millisecondsBetweenEndAndStartDates > MILLISECONDS_IN_DAY_THRESHOLD) {
      endDate = subMilliseconds(
        endDate,
        millisecondsBetweenEndAndStartDates - MILLISECONDS_IN_DAY_THRESHOLD,
      )
    } else if (millisecondsBetweenEndAndStartDates < MILLISECONDS_IN_DAY_THRESHOLD) {
      endDate = addMilliseconds(
        endDate,
        MILLISECONDS_IN_DAY_THRESHOLD - millisecondsBetweenEndAndStartDates,
      )
    }

    return {
      reservationLocationId,
      timeRange: {startDate, endDate},
      partySize,
      experienceId,
    }
  }

  let startDate = new Date(date)
  let endDate = new Date(date)

  if (period === 'day') {
    startDate = startOfDay(startDate)
    endDate = endOfDay(endDate)
  } else {
    startDate = subMinutes(startDate, MINUTES_IN_HALF_DAY)
    endDate = addMinutes(startDate, MINUTES_IN_DAY - 1)
  }

  // Remove milliseconds and seconds
  startDate = setMilliseconds(setSeconds(startDate, 0), 0)
  endDate = setMilliseconds(setSeconds(endDate, 0), 0)

  // Detect "daylight saving time"
  if (startDate.getTimezoneOffset() !== endDate.getTimezoneOffset()) {
    endDate = subMinutes(endDate, MINUTES_IN_HOUR)
  }

  // TODO: remove one extra hour from endDate to test server errors
  endDate = subMinutes(endDate, 15)

  if (timeZone) {
    startDate = zonedTimeToUtc(startDate, timeZone)
    endDate = zonedTimeToUtc(endDate, timeZone)
  }

  return {
    reservationLocationId,
    timeRange: {startDate, endDate},
    partySize,
    experienceId,
  }
}

const getScheduledTimeSlots = async (
  flowAPI: ControllerFlowAPI,
  params: GetScheduledTimeSlotsRequest,
): Promise<TimeSlot[]> => {
  const getScheduledTimeSlotsTransaction = flowAPI.panoramaClient.transaction(
    TransactionNames.getScheduledTimeSlots,
  )

  getScheduledTimeSlotsTransaction.start({
    params,
  })

  const {withErrorHandler, getResolvedError} = flowAPI.errorHandler
  try {
    const response = await withErrorHandler(
      () => flowAPI.httpClient.request(getScheduledTimeSlotsService(params)),
      {
        errorCodesMap: {},
      },
    )

    getScheduledTimeSlotsTransaction.finish()

    return response.data.timeSlots ?? []
  } catch (error: any) {
    flowAPI.panoramaClient.errorMonitor().reportError(error)

    getResolvedError(error)
    flowAPI.panoramaClient.errorMonitor().reportError(error as Error, {
      context: {
        requestParams: params,
      },
    })

    throw error
  }
}

const normalizeTimeSlots = ({
  timeSlots,
  timeZone,
}: {
  timeSlots: TimeSlot[]
  timeZone?: string | null
}) =>
  timeSlots.map((t) => ({
    ...t,
    startDate: utcToZonedTime(t.startDate!, timeZone),
  }))
